'use client';

import { Button, ButtonGroup, Flex, Loading, Text } from '@/_components';
import { type FC, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import type { ExpiredLinkProps } from './ExpiredLink.types';
import { getAuthMethods, sendSMSVerificationCode } from '@/_api/auth';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { jwtDecode } from 'jwt-decode';

export const ExpiredLink: FC<ExpiredLinkProps> = () => {
  const { setLoginState, setPreviousLoginState, setUid, uid } =
    useContext(LoginContext);
  const { addToast } = useContext(ToastContext);
  const { sendMagicLink } = useContext(UserContext);
  const [authMethods, setAuthMethods] = useState<any>(undefined);
  const searchParams = useSearchParams();
  const [accountantId, setAccountantId] = useState<number | undefined>(
    undefined,
  );
  const [redirect, setRedirect] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [methodChosen, setMethodChosen] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (searchParams) {
      let decodedToken: any;
      const authToken = searchParams.get('auth_token');

      if (authToken) {
        decodedToken = jwtDecode(authToken);
      }

      const decodedUid = decodedToken?.uid;

      if (decodedUid) {
        setAccountantId(
          // Both accountant_id and partner_id mean the ID of the advisor user
          decodedToken?.accountant_id || decodedToken?.partner_id,
        );
        setRedirect(searchParams.get('redirect'));
        setUid(decodedUid);

        const fetchAuthMethods = async () => {
          try {
            setIsLoading(true);
            const response: any = await getAuthMethods(decodedUid);
            const { methods } = response;
            setAuthMethods(methods);
          } catch (error) {
            addToast('error', 'Something went wrong', { error });
          } finally {
            setIsLoading(false);
          }
        };

        fetchAuthMethods();
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendAuthLink = async () => {
    if (!uid) {
      return addToast('error', 'Something went wrong', {
        error: 'No UID found',
      });
    }

    if (!accountantId) {
      return addToast('error', 'Something went wrong', {
        error: 'No accountant ID found',
      });
    }

    setIsLoading(true);
    await sendMagicLink(accountantId, redirect, uid);
    setMethodChosen('email');
    setIsLoading(false);
  };

  const sendSmsCode = async () => {
    try {
      if (uid) {
        setIsLoading(true);

        const response = (await sendSMSVerificationCode({
          uid,
        })) as unknown as Response; // TODO: `_callBackend` refactoring needed. See SOR-13473.

        const { status } = response;

        if (status === 429) {
          addToast('error', (await response.json())?.message);
          setIsLoading(false);
          return;
        }

        setPreviousLoginState('expired-link');
        setLoginState('sent-text');
      } else {
        addToast('error', 'Something went wrong', {
          error: 'No UID found',
        });
      }
    } catch (error) {
      addToast(
        'error',
        'Sorry, there was a problem sending your verification code. Please try again.',
        {
          error,
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onBack = () => setMethodChosen(undefined);

  // This is a simple obfuscation function that replaces all asterisks with a dot since the asterisk isn't as clean as a dot
  const improveObfuscation = (sensitivePhrase: string) => {
    if (!sensitivePhrase) return '';
    return sensitivePhrase.replace(/\*/g, '•');
  };

  const emailMethod =
    authMethods &&
    authMethods.length > 0 &&
    authMethods.find((method: { name: string }) => method.name === 'email');

  const phoneMethod =
    authMethods &&
    authMethods.length > 0 &&
    authMethods.find((method: { name: string }) => method.name === 'phone');

  const obfuscatedEmail = improveObfuscation(emailMethod?.value);
  const obfuscatedPhone = improveObfuscation(phoneMethod?.value);

  return (
    <Flex isColumn={true} gap={8} padding={10} isCentered={true}>
      {isLoading ? (
        <Loading size={24} />
      ) : (
        <>
          {methodChosen ? (
            <>
              <Text type="small" color="secondary">
                {methodChosen === 'email' && 'Sent an email to'}{' '}
                <b>{emailMethod.value}</b>.
              </Text>

              <ButtonGroup>
                <Button
                  value="Back"
                  type="button"
                  onClick={onBack}
                  intent="secondary"
                />
              </ButtonGroup>
            </>
          ) : (
            <>
              {typeof authMethods !== 'undefined' && (
                <>
                  <Flex isColumn={true} gap={8}>
                    <Text type="small" color="primary" fontWeight={'semi-bold'}>
                      For additional security to safeguard your information, we
                      need to first verify your account.
                    </Text>
                    <Text type="small" color="primary">
                      {emailMethod &&
                        phoneMethod &&
                        'You can choose to have a sign-in link sent via email or a verification code sent via SMS.'}
                      {emailMethod &&
                        !phoneMethod &&
                        'Click the button to receive a sign-in link via email.'}
                      {!emailMethod &&
                        phoneMethod &&
                        'Click the button to receive a verification code via SMS.'}
                    </Text>
                  </Flex>

                  <ButtonGroup
                    isFullWidth={true}
                    align={'center'}
                    isSameLine={emailMethod && phoneMethod}
                  >
                    {emailMethod && (
                      <Flex isColumn={true} gap={4} isFullWidth={true}>
                        <Button
                          intent="secondary"
                          onClick={sendAuthLink}
                          value={'Email Sign-in Link'}
                        />

                        <Text type="tiny" color="primary" textAlign={'center'}>
                          We&apos;ll email {obfuscatedEmail}
                        </Text>
                      </Flex>
                    )}

                    {phoneMethod && (
                      <Flex isColumn={true} gap={4} isFullWidth={true}>
                        <Button
                          intent="secondary"
                          onClick={sendSmsCode}
                          value={'Text Verification Code'}
                        />
                        <Text type="tiny" color="primary" textAlign={'center'}>
                          We&apos;ll text {obfuscatedPhone}
                        </Text>
                      </Flex>
                    )}

                    {/* This is a fallback that shouldn't come up but is here just in case */}
                    {!emailMethod && !phoneMethod && (
                      <Button
                        iconRight="arrow-right"
                        intent="secondary"
                        onClick={() => window.location.assign('/')}
                        value={'Request a new link'}
                      />
                    )}
                  </ButtonGroup>

                  {emailMethod && (
                    <Flex isColumn={true}>
                      <Text type="small" color="secondary" textAlign={'center'}>
                        Can’t find your verification email?
                        <br />
                        Make sure to check your spam folder!
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Flex>
  );
};
